import { Component, Inject, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CreateShareQuoteRequest, QuoteShareColumns } from '@app/quoting/models/ShareQuote/CreateShareQuoteRequest';
import { ShareQuoteService } from '@app/quoting/services/share-quote.service';
import { AlertComponent } from '@app/shared/components/alert/alert.component';
import { from } from 'rxjs';
import { LoadingModalComponent, LoadingModalData } from '../../../shared/components/loading-modal/loading-modal.component';
import { ShareQuote } from '../../models/ShareQuote/ShareQuote';
import { QuoteRequestType } from '../../../shared/models/QuoteRequestType';

@Component({
  selector: 'app-share-quote-dialog',
  templateUrl: './share-quote-dialog.component.html',
  styleUrls: ['./share-quote-dialog.component.scss']
})
export class ShareQuoteDialogComponent {

  shareQuote: ShareQuote;
  @ViewChild('tooltip') tooltip;

  shareQuoteForm = new FormGroup(
    {
      customerEmail: new FormControl('', [Validators.email, Validators.required]),
      configuration: new FormGroup({
        supplier: new FormControl(false),
        productType: new FormControl(false),
        contractLength: new FormControl(true),
        usage: new FormControl(false),
        totalCost: new FormControl(false),
        annualCost: new FormControl(false),
        sweetSpot: new FormControl(true),
        rate: new FormControl(true),
        annualCommission: new FormControl(false),
        totalCommission: new FormControl(false),
      }, [this.hasCheck()])
    }
  )

  private quoteRequestId: string;

  constructor(
    public dialogRef: MatDialogRef<ShareQuoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private shareQuoteService: ShareQuoteService,
    protected dialog: MatDialog,
  ) {
    this.quoteRequestId = data.quoteRequestId;

    if ((data.quoteType as string).replace(/\s/g, "") == QuoteRequestType.QuickQuote)
      (this.shareQuoteForm.get('configuration') as FormGroup).removeControl('productType');
  }

  hasCheck(): ValidatorFn {
    return (control:AbstractControl) : ValidationErrors | null => {
      let checkList = this.shareQuoteForm?.get("configuration");
      if(!checkList)
        return {nocheckboxes:true}

      const isValid = Object.values(checkList.value).some(_ => _ == true);

      return isValid ? null : {nocheckboxes:true};
    }
  }

  async sendEmail() {

    const email = this.shareQuoteForm.get("customerEmail").value;
    this.shareQuoteForm.markAllAsTouched();
    this.shareQuoteForm.updateValueAndValidity();
    if(!email)
    {
      return;
    }

    try {
      if(this.shareQuoteForm.valid) {
          const createRequest: CreateShareQuoteRequest = {
            customerEmail: email,
            quoteShareColumns: this.shareQuoteForm.get("configuration").value as QuoteShareColumns,
          }

          this.shareQuote = await this.createShareQuote(createRequest, `Sending Email to ${createRequest.customerEmail}`);

          // a pop up dialog to inform user email already send.
          this.dialog.open(AlertComponent, {
            width: '30%',
            data: {
              title: 'Email status',
              message: 'Email sent to ' + this.shareQuote.customerEmail,
            },
          })
      }
    } catch (error) {
      this.dialog.open(AlertComponent, {
        width: '30%',
        data: {
          title: 'Error Sending Email',
          message: error,
        },
      })
    }
  }

  async copyLink() {
    this.tooltip.message = "Link Copied to Clipboard";
    this.tooltip.show();
  }

  async generateLink() {

    this.shareQuoteForm.markAllAsTouched();
    this.shareQuoteForm.updateValueAndValidity();
    try {
      if(this.shareQuoteForm.get("configuration").valid) {
        const createRequest: CreateShareQuoteRequest = {
          customerEmail: this.shareQuoteForm.get("customerEmail").value,
          quoteShareColumns: this.shareQuoteForm.get("configuration").value as QuoteShareColumns,
        }

        this.shareQuote = await this.createShareQuote(createRequest, 'Generating Link');
      }
    } catch (error) {
      this.dialog.open(AlertComponent, {
        width: '30%',
        data: {
          title: 'Generating Link Status',
          message: error,
        },
      })
    }

  }

  async downloadExportedData() {

    if (this.shareQuoteForm.get("configuration").valid) {
      const createRequest: CreateShareQuoteRequest = {
        quoteShareColumns: this.shareQuoteForm.get("configuration").value as QuoteShareColumns,
      }
      await this.exportShareQuote(createRequest, 'Exporting data');

    }
  }

  async exportShareQuote(createRequest: CreateShareQuoteRequest, message: string) {

    this.shareQuoteForm.disable();

    let result = await this.dialog
      .open(LoadingModalComponent, {
        width: '25%',
        disableClose: true,
        data: {
          message: message,
          unitOfWork: from(this.shareQuoteService.export(Number(this.quoteRequestId), createRequest)),
        } as LoadingModalData,
      })
      .afterClosed()
      .toPromise();

    const fileName = 'Sales360Quotes_' + this.quoteRequestId + "_" + (new Date).toLocaleDateString().split('T')[0] + '.xlsx';
    this.downloadData(result, fileName);

  }

  private downloadData(data : any,  fileName: string) {
    const linkSource = window.URL.createObjectURL(data);
    const downloadLink = document.createElement('a');

    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }


  async createShareQuote(createRequest: CreateShareQuoteRequest, message: string): Promise<ShareQuote> {

    this.shareQuoteForm.disable();

    let results = await this.dialog
      .open(LoadingModalComponent, {
        width: '25%',
        disableClose: true,
        data: {
          message: message,
          unitOfWork: from(this.shareQuoteService.post(Number(this.quoteRequestId), createRequest)),
        } as LoadingModalData,
      })
      .afterClosed()
      .toPromise();

    return results;

  }
}
